import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import type { Draft } from 'immer';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { RootState } from '../../../../reducers';
import {
  addWebhookUrlThunk as addUserWebhookThunk,
  removeWebhookUrlThunk as removeUserWebhookThunk,
} from '../../settingsSlice';
import {
  addWebhookUrlThunk as addTeamWebhookThunk,
  removeWebhookUrlThunk as removeTeamWebhookThunk,
} from '../../../team/teamSlice';
import Webhooks from './webhookComponents/Webhooks';
import { Webhook } from '../../../../types/state';
import { useLimits } from '../../../plan/hooks/limits';
import templates from '../../../selectedVideo/tabs/templates/templates';
import {
  fetchTemplates,
  selectAllTemplates,
  Template,
} from '../../../selectedVideo/templateSlice';

type WebhookType = 'team' | 'user';
type WebhookWithType = Draft<Webhook> & { type: WebhookType };

// Add helper functions at the top level
const isTemplateCustomized = (
  templateId: string,
  savedTemplates: Template[],
) => {
  return savedTemplates.some((t) => t.id === templateId);
};

const isBuiltInTemplate = (templateId: string) => {
  return templates.some((t) => t.id === templateId);
};

const WebhooksIndex: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const guardLimit = useLimits();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState({
    webhookName: '',
    webhookUrl: '',
    webhookType: 'team' as WebhookType,
    webhookTemplateId: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { team } = useSelector((state: RootState) => state);
  const { teamId } = team.selectedTeam || {};
  const { integrations } = team.selectedTeam || {};
  const teamWebhooks = integrations?.webhooks || [];
  const userWebhooks = useSelector(
    (state: RootState) => state.settings.user.integrations.webhooks,
  );
  const savedTemplates = useSelector(selectAllTemplates);

  // Combine webhooks and add type information
  const allWebhooks: WebhookWithType[] = [
    ...teamWebhooks.map((webhook) => ({ ...webhook, type: 'team' as const })),
    ...userWebhooks.map((webhook) => ({ ...webhook, type: 'user' as const })),
  ];

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  const handleAddWebhook = () => {
    // Prevent double-submission
    if (isSubmitting) return;

    guardLimit('integrations', async () => {
      const { webhookName, webhookUrl, webhookType, webhookTemplateId } =
        formState;

      if (!webhookUrl || !webhookName) {
        setError(t('settings.integrations.webhooks.index.errors.fillFields'));
        return;
      }

      if (!webhookUrl.startsWith('https://')) {
        setError(
          t('settings.integrations.webhooks.index.errors.httpsRequired'),
        );
        return;
      }

      setIsSubmitting(true);
      try {
        if (webhookType === 'team') {
          if (!teamId) {
            setError(
              t('settings.integrations.webhooks.index.errors.noTeamSelected'),
            );
            return;
          }
          await dispatch(
            addTeamWebhookThunk({
              teamId,
              name: webhookName,
              url: webhookUrl,
              templateId: webhookTemplateId,
            }),
          );
        } else {
          await dispatch(
            addUserWebhookThunk({
              name: webhookName,
              url: webhookUrl,
              templateId: webhookTemplateId,
            }),
          );
        }
        setDialogOpen(false);
        setFormState({
          webhookName: '',
          webhookUrl: '',
          webhookType: 'team',
          webhookTemplateId: '',
        });
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('settings.integrations.webhooks.index.errors.addFailed'),
        );
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  const handleRemoveWebhook = async (url: string, type: WebhookType) => {
    try {
      if (type === 'team') {
        if (!teamId) return;
        await dispatch(removeTeamWebhookThunk({ teamId, url }));
      } else {
        await dispatch(removeUserWebhookThunk({ url }));
      }
    } catch (err) {
      console.error('Failed to remove webhook:', err);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormState({
      webhookName: '',
      webhookUrl: '',
      webhookType: 'team',
      webhookTemplateId: '',
    });
    setError(null);
    setIsSubmitting(false);
  };

  // Only show custom templates in the saved tab
  const customTemplates = savedTemplates.filter(
    (template) =>
      !isBuiltInTemplate(template.id) ||
      isTemplateCustomized(template.id, savedTemplates),
  );

  return (
    <div className="space-y-4">
      <Webhooks
        webhooks={allWebhooks}
        onRemoveWebhook={(url, type) => handleRemoveWebhook(url, type)}
        note={t('settings.integrations.webhooks.teamNote')}
        emptyStateButton={
          <Button
            variant="minimal"
            onClick={() => setDialogOpen(true)}
            className="text-black hover:text-black"
          >
            {t('settings.integrations.webhooks.index.buttons.addWebhook')}
          </Button>
        }
      />

      {allWebhooks.length > 0 && (
        <div className="flex justify-end mt-6">
          <Button
            variant="minimal"
            onClick={() => setDialogOpen(true)}
            className="text-black hover:text-black"
          >
            {t('settings.integrations.webhooks.index.buttons.addWebhook')}
          </Button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('settings.integrations.webhooks.index.dialog.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Webhook Name */}
            <div className="space-y-2">
              <Label htmlFor="webhook-name">
                {t(
                  'settings.integrations.webhooks.index.form.webhookName.label',
                )}
              </Label>
              <Input
                id="webhook-name"
                placeholder={t(
                  'settings.integrations.webhooks.index.form.webhookName.placeholder',
                )}
                value={formState.webhookName}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    webhookName: e.target.value,
                  }))
                }
                className={error ? 'border-destructive' : ''}
              />
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <Label htmlFor="webhook-url">
                {t(
                  'settings.integrations.webhooks.index.form.webhookUrl.label',
                )}
              </Label>
              <Input
                id="webhook-url"
                placeholder={t(
                  'settings.integrations.webhooks.index.form.webhookUrl.placeholder',
                )}
                value={formState.webhookUrl}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    webhookUrl: e.target.value,
                  }))
                }
                className={error ? 'border-destructive' : ''}
              />
            </div>

            {/* Webhook Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t(
                  'settings.integrations.webhooks.index.form.webhookType.label',
                )}
              </Label>
              <Select
                value={formState.webhookType}
                onValueChange={(value: 'team' | 'user') =>
                  setFormState((prev) => ({
                    ...prev,
                    webhookType: value,
                    webhookTemplateId:
                      value === 'user' ? '' : prev.webhookTemplateId,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">
                    {t(
                      'settings.integrations.webhooks.index.form.webhookType.team.title',
                    )}
                  </SelectItem>
                  <SelectItem value="user">
                    {t(
                      'settings.integrations.webhooks.index.form.webhookType.private.title',
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formState.webhookType === 'team'
                  ? t(
                      'settings.integrations.webhooks.index.form.webhookType.team.description',
                    )
                  : t(
                      'settings.integrations.webhooks.index.form.webhookType.private.description',
                    )}
              </p>
            </div>

            {/* Template Selection */}
            <div className="space-y-2">
              <Label htmlFor="template-select">
                {t('settings.integrations.webhooks.index.form.template.label')}
              </Label>
              <Select
                value={formState.webhookTemplateId || 'none'}
                onValueChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    webhookTemplateId: value === 'none' ? '' : value,
                  }))
                }
                disabled={formState.webhookType === 'user'}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'settings.integrations.webhooks.index.form.template.none',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t(
                      'settings.integrations.webhooks.index.form.template.none',
                    )}
                  </SelectItem>
                  {customTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Error Message */}
            {error && <div className="text-sm text-destructive">{error}</div>}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('settings.integrations.webhooks.index.dialog.buttons.cancel')}
            </Button>
            <Button onClick={handleAddWebhook} disabled={isSubmitting}>
              {isSubmitting
                ? t('common.submitting', { defaultValue: 'Submitting...' })
                : t('settings.integrations.webhooks.index.dialog.buttons.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhooksIndex;
